import type { ApprovalDegree, ApprovalOddsData } from '../types/approvalOdds';

/**
 * Rank used to order products by approval odds, from best ("HIGH") to worst.
 * Products without an odds record are treated as "UNKNOWN" (lowest rank).
 */
const APPROVAL_DEGREE_ORDER: Record<ApprovalDegree, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
  UNKNOWN: 3,
};

/**
 * Sorts products by approval odds degree from "HIGH" to "LOW"
 * (HIGH -> MEDIUM -> LOW -> UNKNOWN) when `sortByApprovalOdds` is enabled and
 * `approvalOddsData` contains any odds records. Products with the same degree,
 * or with no odds record, keep their original relative order (stable sort).
 *
 * Otherwise the original product order is returned unchanged.
 *
 * @param products The products to sort.
 * @param getTitle Resolves the key used to look up a product's odds record.
 * @param approvalOddsData The approval odds data keyed by product title.
 * @param sortByApprovalOdds Whether sorting by approval odds is enabled.
 */
export function sortProductsByApprovalOdds<T>(
  products: T[],
  getTitle: (product: T) => string | undefined,
  approvalOddsData: ApprovalOddsData | null | undefined,
  sortByApprovalOdds: boolean | undefined,
): T[] {
  const odds = approvalOddsData?.odds;

  if (!sortByApprovalOdds || !odds || Object.keys(odds).length === 0) {
    return products;
  }

  const getRank = (product: T): number => {
    const title = getTitle(product);
    const degree = title ? odds[title] : undefined;
    // Fall back to UNKNOWN for missing records or any unexpected value coming
    // from the consuming app, so the comparator never returns NaN.
    return APPROVAL_DEGREE_ORDER[degree as ApprovalDegree] ??
      APPROVAL_DEGREE_ORDER.UNKNOWN;
  };

  return products
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      const rankDiff = getRank(a.product) - getRank(b.product);
      // Preserve the original order for products with the same rank.
      return rankDiff !== 0 ? rankDiff : a.index - b.index;
    })
    .map(({ product }) => product);
}
